import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete,Card,Fab,IconButton,Skeleton,Stack,TextField } from "@mui/material";
import { Handle,Position,useEdges,useNodes } from "@xyflow/react";
import { useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import type { customNodeType } from "../../types/fluxo/types";

const handleStyle = {
  width: 12,
  height: 12,
  background: '#1976d2',
  border: '2px solid #fff',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(2)',
    background: '#1565c0',
  }
};

export default function CustomNode({ id, data }: customNodeType) {
  const { t } = useTranslation('translation');
  const [showRightSkeleton, setRightShowSkeleton] = useState(false);
  const [selected, setSelected] = useState<{ id: string, nome: string }>();
  const [isHovered, setIsHovered] = useState(false);
  const edges = useEdges();
  const nodes = useNodes();
  const selectedFormIds = new Set(
    nodes
      .filter((node) => node.id !== id)
      .map((node) => node.id),
  );

  useEffect(() => {
    const id = data?.select?.find((ele: { id: string, nome: string }) => ele.id === data.id);
    if (data?.select?.includes(id as { id: string, nome: string })) {
      setSelected({
        id: data.id,
        nome: data.select.find((ele: { id: string, nome: string }) => ele.id === data.id)?.nome || ''
      });
    }
  }, [data.id, data.select]);

  const hasOutgoingEdge = edges.some((edge: { source: string }) => edge.source === id);

  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between' width='100%'>
      <Card sx={{
        padding: 1.5,
        width: '350px',
        border: isHovered ? '1px solid #1976d2' : '1px solid #e0e0e0',
        boxShadow: isHovered ? 6 : 1,
        transition: 'all 0.3s ease',
        zIndex: 1
      }}>
        <Stack
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          direction='row'
          width='100%'
          alignItems='center'
          justifyContent='space-between'
          spacing={2}>
          <Stack direction='row' alignContent='center' width='100%' spacing={2} justifyContent='space-between'>
            <Autocomplete
              className="nodrag"
              getOptionLabel={option => option.nome as string}
              getOptionKey={option => option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disablePortal={false}
              onMouseDown={(e) => e.stopPropagation()}
              fullWidth
              value={selected || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  data.onChangeLabel(id, newValue.id, newValue.nome);
                  setSelected(newValue);
                } else {
                  setSelected({ id: '', nome: '' });
                }
              }}
              options={([...new Set(data.select)] as { id: string, nome: string, fluxo: string }[])
                ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                ?.filter((ele) => !ele.fluxo && !selectedFormIds.has(ele.id))
                ?.map((ele) => {
                  return {
                    id: ele.id,
                    nome: ele.nome
                  };
                }) || []}
              renderInput={(params) => <TextField {...params} onMouseDown={(e) => e.stopPropagation()} label={t('fluxo.selectForms')} />}
            />
            <IconButton
              disabled={nodes.length <= 1}
              onClick={() => data.onDelete(data.id)}
              size="small"
              edge="end"
              color="error"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          {!hasOutgoingEdge && <Fab
            onClick={() => {
              data?.onAddNode(id);
              setRightShowSkeleton(false);
            }}
            onMouseEnter={() => setRightShowSkeleton(true)}
            onMouseLeave={() => setRightShowSkeleton(false)}
            size="small">
            <AddIcon />
          </Fab>}
        </Stack>
        <Handle
          type="target"
          position={Position.Left}
          style={{
            ...handleStyle,
            left: -6,
            opacity: isHovered ? 1 : 0.7
          }}
        />
        <Handle
          type="source"
          position={Position.Right}
          style={{
            ...handleStyle,
            right: -6,
            opacity: isHovered ? 1 : 0.7
          }}
        />
      </Card >
      {showRightSkeleton && !hasOutgoingEdge && <Skeleton
        variant='rounded'
        width='350px'
        height='70px'
        sx={{ marginLeft: 2, opacity: 0.6 }}
      />
      }
    </Stack >
  );
}